import React, {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.bubble.css';
import styled from 'styled-components';

import MetricOptionsContext from 'contexts/MetricOptionsContext';

import { NO_DRAG_CLASS } from './constants';
import MarkdownText from './MarkdownText';
import {
  METRIC_CHIP_CLASS,
  chipTypedPlaceholders,
  fromEditorHtml,
  getEditorText,
  insertMetricChip,
  labelMetricChips,
  markSelectedChips,
  selectPressedChip,
  spaceAfterChip,
  toEditorHtml,
} from './metricChips';
import MetricValueReporters from './MetricValueReporters';
import { TEXT_COLOR_CSS } from './textColorFormat';
// Registers the size a heading gives part of a line
import './textSizeFormat';
import {
  PlaceholderSuggestionList,
  usePlaceholderSuggestions,
} from './PlaceholderSuggestions';
import {
  SubtextPlaceholder,
  escapeMarkdown,
  fillHtmlPlaceholders,
  fillPlaceholders,
  getAvailablePlaceholders,
  getPlaceholderQuery,
  getReferencedMeasures,
  withComparisonPlaceholders,
} from './subtextPlaceholders';
import type {
  BraceMetricPicker,
  StudioTextHandle,
  TextVerticalAlign,
} from './studioTextTypes';
import { PickerAnchor, TypingHint } from './textPickerAnchor';
import useComparisonPeriod from './useComparisonPeriod';

export type { StudioTextHandle, TextVerticalAlign } from './studioTextTypes';

// Quill's formatting bubble, shown over selected text. No colour picker: it
// writes hex colours into the text, which ignore the theme (and dark mode).
const TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ align: [] }],
  ['link', 'clean'],
];

const MODULES = { toolbar: TOOLBAR };
// Where the block has its own formatting panel instead (see TextFormatPanel)
const MODULES_WITHOUT_BUBBLE = { toolbar: false };

// What Quill holds when everything has been deleted
const EMPTY_QUILL_HTML = '<p><br></p>';

// The editor scrolls itself, so the formatting bubble (a sibling of it) can
// overflow the block while editing (see CardShell's $isEditing). The bubble
// is restyled as one of our menus; its rules triple the wrapper's class so
// they beat Quill's own hover/active/focus colours (e.g. a white active
// heading label) whichever stylesheet loads last.

const Wrapper = styled.div<{
  $isEditing: boolean;
  $verticalAlign: TextVerticalAlign;
}>`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  cursor: ${(props) => (props.$isEditing ? 'text' : 'inherit')};

  .quill,
  .ql-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    font-family: inherit;
    font-size: 14px;
  }

  .ql-editor {
    flex: 1;
    min-height: 0;
    padding: 0;
    overflow-y: ${(props) => (props.$isEditing ? 'auto' : 'hidden')};
    color: var(--color-text-primary);
    line-height: 1.4;
  }

  /* A metric's placeholder while editing: its name in braces, formatted
     as the text around it (an inline-block takes no underline from it, so
     that's drawn for it). Pressed, it's selected to format. */
  .${METRIC_CHIP_CLASS} {
    display: inline-block;
    margin: 0 1px;
    padding: 0 0.4em;
    border-radius: 0.4em;
    border: 1px solid var(--color-border-primary);
    background-color: var(--color-bg-soft);
    line-height: 1.4;
    white-space: nowrap;
    cursor: pointer;
  }

  .${METRIC_CHIP_CLASS}[data-selected] {
    box-shadow: 0 0 0 2px var(--color-studio-selection);
  }

  .${METRIC_CHIP_CLASS}::before {
    content: '{' attr(data-name) '}';
    color: var(--color-text-secondary);
  }

  u .${METRIC_CHIP_CLASS}::before {
    text-decoration: underline;
  }

  /* Coloured, it takes its text's colour */
  [class*='studio-color-']
    .${METRIC_CHIP_CLASS}::before,
    [style*='color']
    .${METRIC_CHIP_CLASS}::before {
    color: inherit;
  }

  ${TEXT_COLOR_CSS}

  a .${METRIC_CHIP_CLASS}::before {
    color: var(--color-text-link);
    text-decoration: underline;
  }

  /* Centred vertically, as the classic Text card's Center align, or at the
     foot of the block, its paragraphs kept one under another */
  ${(props) =>
    props.$verticalAlign !== 'top' &&
    `
    .ql-editor {
      display: flex;
      flex-direction: column;
      justify-content: ${
        props.$verticalAlign === 'bottom' ? 'flex-end' : 'center'
      };
    }
  `}

  .ql-editor.ql-blank::before {
    left: 0;
    right: 0;
    font-style: normal;
    color: var(--color-text-tertiary);
  }

  html[data-theme='dark'] & .ql-editor {
    [style*='color: rgb(0, 0, 0)'],
    [style*='color: black'] {
      color: var(--color-text-primary) !important;
    }
  }

  /* One line however narrow the block: it's positioned inside the editor,
     which would otherwise cap its width and wrap the buttons */
  &&& .ql-bubble .ql-tooltip {
    z-index: 10;
    width: max-content;
    padding: 0 4px;
    border-radius: 8px;
    border: 1px solid var(--color-border-primary);
    background-color: var(--color-bg-primary);
    color: var(--color-text-primary);
    box-shadow: var(--shadow-dropdown);
  }

  &&& .ql-bubble .ql-tooltip-arrow {
    display: none;
  }

  &&& .ql-bubble .ql-toolbar .ql-formats {
    margin: 4px 2px;
  }

  &&& .ql-bubble .ql-toolbar button {
    border-radius: 4px;
  }

  &&& .ql-bubble .ql-toolbar button:hover,
  &&& .ql-bubble .ql-picker-label:hover {
    background-color: var(--color-bg-hover);
  }

  &&& .ql-bubble .ql-stroke,
  &&& .ql-bubble .ql-stroke-miter {
    stroke: var(--color-icon-secondary);
  }

  &&& .ql-bubble .ql-fill,
  &&& .ql-bubble .ql-stroke.ql-fill {
    fill: var(--color-icon-secondary);
  }

  &&& .ql-bubble .ql-picker,
  &&& .ql-bubble .ql-picker-label,
  &&& .ql-bubble .ql-picker-item {
    color: var(--color-text-secondary);
  }

  &&& .ql-bubble .ql-picker-label {
    border-radius: 4px;
  }

  &&& .ql-bubble button:hover .ql-stroke,
  &&& .ql-bubble button.ql-active .ql-stroke,
  &&& .ql-bubble button:hover .ql-stroke-miter,
  &&& .ql-bubble button.ql-active .ql-stroke-miter,
  &&& .ql-bubble .ql-picker-label:hover .ql-stroke,
  &&& .ql-bubble .ql-picker-label.ql-active .ql-stroke,
  &&& .ql-bubble .ql-picker-item:hover .ql-stroke,
  &&& .ql-bubble .ql-picker-item.ql-selected .ql-stroke {
    stroke: var(--color-icon-primary);
  }

  &&& .ql-bubble button:hover .ql-fill,
  &&& .ql-bubble button.ql-active .ql-fill,
  &&& .ql-bubble button:hover .ql-stroke.ql-fill,
  &&& .ql-bubble button.ql-active .ql-stroke.ql-fill {
    fill: var(--color-icon-primary);
  }

  &&& .ql-bubble .ql-picker-label:hover,
  &&& .ql-bubble .ql-picker-label.ql-active,
  &&& .ql-bubble .ql-picker-item:hover,
  &&& .ql-bubble .ql-picker-item.ql-selected {
    color: var(--color-text-primary);
  }

  &&& .ql-bubble .ql-picker-options {
    margin-top: 4px;
    border-radius: 8px;
    border: 1px solid var(--color-border-primary);
    background-color: var(--color-bg-primary);
    box-shadow: var(--shadow-dropdown);
  }

  &&& .ql-bubble .ql-tooltip-editor input[type='text'] {
    color: var(--color-text-primary);
  }

  &&& .ql-bubble .ql-tooltip-editor a::before {
    color: var(--color-icon-secondary);
  }
`;

// Rich text for a Text block or a Composite's Text element. Read-only until
// its block (or element) is selected, then typed into in place, the caret
// put at the end of the text ready to type; selecting text brings up the
// formatting bubble (a Text block has a panel instead). While editing it's
// excluded from dragging, so selecting text doesn't move the block. Typing
// '{' suggests the metrics it has, or, where it can add metrics
// (onOpenMetricPicker), opens the library's picker at the '{'.
const NO_PLACEHOLDERS: SubtextPlaceholder[] = [];
// Each chip's metric name, by placeholder key
type ChipNames = Record<string, string>;
const NO_CHIP_NAMES: ChipNames = {};
// Below the line being typed on
const SUGGESTIONS_OFFSET = 4;

// Where a change typed a lone '{', if it did
const getTypedBraceIndex = (delta: unknown): number | undefined => {
  const ops = (delta as { ops?: { retain?: number; insert?: unknown }[] }).ops;
  let index = 0;
  for (const op of ops ?? []) {
    if (op.retain !== undefined) {
      index += op.retain;
    } else {
      return op.insert === '{' ? index : undefined;
    }
  }
  return undefined;
};

// The typed text from start to the caret (none, from past it) replaced by
// the placeholder's chip, as the user's edit (so it saves), then a space and
// the caret (see spaceAfterChip). Without a caret, it goes at the end.
const replaceWithChip = ({
  editor,
  start,
  key,
}: {
  editor: ReturnType<ReactQuill['getEditor']>;
  start: number;
  key: string;
}) => {
  const range = editor.getSelection(true);
  const caret = range ? range.index : editor.getLength() - 1;
  const from = Math.min(start, caret);
  // Don't leave a closing brace that's already there
  const hasClosingBrace =
    from < caret && getEditorText({ editor, index: caret, length: 1 }) === '}'
      ? 1
      : 0;
  editor.deleteText(from, caret - from + hasClosingBrace, 'user');
  insertMetricChip({ editor, index: from, key });
  spaceAfterChip({ editor, index: from });
};

const StudioText = ({
  id,
  text,
  displayText,
  placeholders = NO_PLACEHOLDERS,
  chipNames = NO_CHIP_NAMES,
  isEditing,
  verticalAlign = 'top',
  isFormatBubbleShown = true,
  editorRef,
  metricPicker,
  onChange,
}: {
  id: string;
  text: string | undefined;
  // Shown instead of the text while not editing (e.g. with its
  // placeholders filled in)
  displayText?: string;
  // Suggested while a {placeholder} is typed, and shown as chips while
  // it's edited
  placeholders?: SubtextPlaceholder[];
  chipNames?: ChipNames;
  isEditing: boolean;
  verticalAlign?: TextVerticalAlign;
  // Selecting text brings up its formatting, unless it has its own
  // panel (see TextFormatPanel)
  isFormatBubbleShown?: boolean;
  editorRef?: React.MutableRefObject<StudioTextHandle | null>;
  // A Text block's library picker, which a '{' typed opens at it: the card
  // inserts what's picked in place of the '{' (see editorRef)
  metricPicker?: BraceMetricPicker;
  onChange: (text: string) => void;
}) => {
  const quillRef = useRef<ReactQuill | null>(null);
  const [suggestionsAt, setSuggestionsAt] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  // Where the '{' that opened the metric picker is, within the editor: its
  // line, and the caret after it
  const [pickerAt, setPickerAt] = useState<{
    top: number;
    bottom: number;
    left: number;
    caretLeft: number;
  }>({ top: 0, bottom: 0, left: 0, caretLeft: 0 });
  // Where the '{' that opened the metric picker is, which what's picked
  // replaces
  const pendingStartRef = useRef<number | undefined>(undefined);

  const onChoose = useCallback(
    ({
      placeholder,
      start,
    }: {
      placeholder: SubtextPlaceholder;
      start: number;
    }) => {
      const editor = quillRef.current?.getEditor();
      if (editor) {
        replaceWithChip({ editor, start, key: placeholder.key });
      }
    },
    [],
  );
  // Where the picker opens instead, its metrics aren't suggested too
  const suggestions = usePlaceholderSuggestions({
    placeholders: metricPicker ? NO_PLACEHOLDERS : placeholders,
    onChoose,
  });
  const { update: updateSuggestions, close: closeSuggestions } = suggestions;

  useImperativeHandle(
    editorRef,
    () => ({
      getEditor: () => quillRef.current?.getEditor(),
      insertPlaceholder: (key: string) => {
        const editor = quillRef.current?.getEditor();
        if (!editor) {
          return;
        }
        // Without one, nothing's replaced: it goes in at the caret
        const start = pendingStartRef.current ?? Number.MAX_SAFE_INTEGER;
        pendingStartRef.current = undefined;
        replaceWithChip({ editor, start, key });
      },
      focus: () => {
        pendingStartRef.current = undefined;
        quillRef.current?.getEditor().focus();
      },
    }),
    [],
  );

  // From the text before the caret, placed under where the '{' was typed
  const syncSuggestions = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    const range = editor?.getSelection();
    if (!editor || !range || placeholders.length === 0 || metricPicker) {
      closeSuggestions();
      return;
    }
    const before = getEditorText({ editor, length: range.index });
    updateSuggestions({ text: before, caret: before.length });
    // Measured only while one's being typed
    const typing = getPlaceholderQuery({ text: before, caret: before.length });
    if (typing) {
      const bounds = editor.getBounds(typing.start);
      if (!bounds) {
        return;
      }
      setSuggestionsAt({
        top: bounds.bottom + SUGGESTIONS_OFFSET,
        left: bounds.left,
      });
    }
  }, [closeSuggestions, metricPicker, placeholders.length, updateSuggestions]);

  // A '{' typed opens the metric picker at it
  const openPickerAt = useCallback(
    (index: number) => {
      const editor = quillRef.current?.getEditor();
      if (!editor || !metricPicker) {
        return;
      }
      const brace = editor.getBounds(index);
      const afterBrace = editor.getBounds(index + 1);
      if (!brace || !afterBrace) {
        return;
      }
      pendingStartRef.current = index;
      setPickerAt({
        top: brace.top,
        bottom: brace.bottom,
        left: brace.left,
        caretLeft: afterBrace.left,
      });
      metricPicker.onOpen();
    },
    [metricPicker],
  );

  useEffect(() => {
    if (!isEditing) {
      closeSuggestions();
      pendingStartRef.current = undefined;
    }
  }, [closeSuggestions, isEditing]);

  // Selected, it's ready to type into: the caret at the end of the text,
  // unless a click in it has already put the caret somewhere. Silently, so
  // the editor isn't scrolled to it.
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (isEditing && editor && !editor.hasFocus()) {
      editor.setSelection(editor.getLength() - 1, 0, 'silent');
    }
  }, [isEditing]);

  // While editing, the editor's HTML (with chips) and the text it stands
  // for, kept up with everything it reports: the user's edits, its own
  // clean-up of the value it's given, and chips made of typed placeholders.
  // While the text is still that, the editor's given back what it has
  // rather than having its content (and caret) reset.
  const lastEditRef = useRef<{ text: string; html: string } | undefined>();
  // The text the editor's content stands for
  const editorTextRef = useRef<string>(text ?? '');
  editorTextRef.current = text ?? '';
  const isEditingRef = useRef<boolean>(isEditing);
  isEditingRef.current = isEditing;
  if (!isEditing) {
    lastEditRef.current = undefined;
  }
  const freshEditorHtml = useMemo(
    () => toEditorHtml({ html: text ?? '', placeholders }),
    [placeholders, text],
  );
  const lastEdit = lastEditRef.current;
  const editorHtml =
    lastEdit && lastEdit.text === (text ?? '')
      ? lastEdit.html
      : freshEditorHtml;

  // Only the user's edits are saved: Quill also reports its own clean-up of
  // the value it was given, which would otherwise feed back into the state.
  // Saved with its chips as placeholders again; placeholders typed out in
  // full become chips.
  const onQuillChange = useCallback(
    (html: string, delta: unknown, source: string) => {
      if (!isEditingRef.current) {
        return;
      }
      if (source !== 'user') {
        lastEditRef.current = { text: editorTextRef.current, html };
        return;
      }
      const saved = html === EMPTY_QUILL_HTML ? '' : fromEditorHtml(html);
      editorTextRef.current = saved;
      lastEditRef.current = { text: saved, html };
      onChange(saved);
      const editor = quillRef.current?.getEditor();
      if (editor) {
        chipTypedPlaceholders({ editor, placeholders });
      }
      const braceIndex = getTypedBraceIndex(delta);
      if (braceIndex !== undefined && metricPicker) {
        openPickerAt(braceIndex);
      } else {
        syncSuggestions();
      }
    },
    [metricPicker, onChange, openPickerAt, placeholders, syncSuggestions],
  );

  const onSelectionChange = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      markSelectedChips({ editor });
    }
    syncSuggestions();
  }, [syncSuggestions]);

  // Its chips named, and those selected marked
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (isEditing && editor) {
      labelMetricChips({ root: editor.root, names: chipNames });
      markSelectedChips({ editor });
    }
  });

  return (
    <Wrapper
      $isEditing={isEditing}
      $verticalAlign={verticalAlign}
      className={isEditing ? NO_DRAG_CLASS : undefined}
      data-testid={`studio-text-${id}`}
      data-editing={isEditing}
      // A metric's chip pressed is selected, to format
      onClick={(e) => {
        const editor = quillRef.current?.getEditor();
        if (isEditing && editor) {
          selectPressedChip({ editor, target: e.target });
        }
      }}
      // Ahead of Quill, so Enter and the arrows work the open list
      onKeyDownCapture={(e) => {
        suggestions.onKeyDown(e);
      }}
    >
      <ReactQuill
        ref={quillRef}
        theme="bubble"
        value={isEditing ? editorHtml : displayText ?? text ?? ''}
        readOnly={!isEditing}
        modules={isFormatBubbleShown ? MODULES : MODULES_WITHOUT_BUBBLE}
        placeholder={
          metricPicker
            ? 'Type something, or { to add a metric'
            : 'Type something…'
        }
        onChange={onQuillChange}
        onChangeSelection={onSelectionChange}
      />
      {isEditing && metricPicker && metricPicker.isOpen && (
        <TypingHint
          style={{
            top: pickerAt.top,
            left: pickerAt.caretLeft,
            height: pickerAt.bottom - pickerAt.top,
          }}
          data-testid={`studio-text-typing-hint-${id}`}
        >
          Select a metric
        </TypingHint>
      )}
      {isEditing && metricPicker && (
        <PickerAnchor style={{ top: pickerAt.top, left: pickerAt.left }}>
          {metricPicker.render(
            <div style={{ height: pickerAt.bottom - pickerAt.top }} />,
          )}
        </PickerAnchor>
      )}
      {isEditing && suggestions.isOpen && (
        <PlaceholderSuggestionList
          matches={suggestions.matches}
          activeIndex={suggestions.activeIndex}
          position={suggestionsAt}
          onHover={suggestions.setActiveIndex}
          onChoose={suggestions.choose}
        />
      )}
    </Wrapper>
  );
};

// What a Text block's {key.delta} placeholders compare with when it hasn't
// picked: the period before its own
export const DEFAULT_TEXT_COMPARISON: Studio.BlockComparison = {
  compareType: 'previous',
};

// Rich text (or Markdown) whose {placeholders} show the values of the given
// metrics (a Text block's own, or a Composite's) while it isn't being
// edited. A Text block's can also show each metric's change from its
// comparison ({key.delta}, {key.percentDelta}). Only the metrics it names
// are fetched, over the dates and filters it sits in, and only those it
// shows the change of over the comparison period.
export const DynamicStudioText = ({
  id,
  text,
  metricIds,
  isEditing,
  verticalAlign,
  isFormatBubbleShown,
  editorRef,
  metricPicker,
  format,
  comparison,
  onChange,
}: {
  id: string;
  text: string | undefined;
  metricIds: string[];
  isEditing: boolean;
  verticalAlign?: TextVerticalAlign;
  isFormatBubbleShown?: boolean;
  editorRef?: React.MutableRefObject<StudioTextHandle | null>;
  metricPicker?: BraceMetricPicker;
  format?: Studio.TextFormat;
  // A Text block's: its placeholders can show changes from it. Absent for a
  // Composite's Text element, which shows values only.
  comparison?: Studio.BlockComparison;
  onChange: (text: string) => void;
}) => {
  const { metricOptionsLookup } = useContext(MetricOptionsContext);
  const isComparable = !!comparison;
  const placeholders = useMemo(() => {
    const available = getAvailablePlaceholders({
      metricIds,
      metricOptionsLookup,
    });
    return isComparable ? withComparisonPlaceholders(available) : available;
  }, [isComparable, metricIds, metricOptionsLookup]);
  const references = useMemo(
    () => (text ? getReferencedMeasures({ text, placeholders }) : []),
    [placeholders, text],
  );
  const against = useComparisonPeriod(comparison);
  const [values, setValues] = useState<Record<string, string>>({});
  const onValue = useCallback(
    ({ key, value }: { key: string; value: string }) =>
      setValues((current) =>
        current[key] === value ? current : { ...current, [key]: value },
      ),
    [],
  );
  const isMarkdown = format === 'markdown';
  const chipNames = useMemo(
    () => Object.fromEntries(placeholders.map((p) => [p.key, p.name])),
    [placeholders],
  );
  const displayText = useMemo(() => {
    if (!text || references.length === 0) {
      return undefined;
    }
    return isMarkdown
      ? fillPlaceholders({
          text,
          placeholders,
          values,
          escape: escapeMarkdown,
        })
      : fillHtmlPlaceholders({ html: text, placeholders, values });
  }, [isMarkdown, placeholders, references.length, text, values]);

  return (
    <>
      <MetricValueReporters
        references={references}
        comparison={against}
        onValue={onValue}
      />
      {isMarkdown ? (
        <MarkdownText
          id={id}
          text={text}
          displayText={displayText}
          placeholders={placeholders}
          isEditing={isEditing}
          verticalAlign={verticalAlign}
          editorRef={editorRef}
          metricPicker={metricPicker}
          onChange={onChange}
        />
      ) : (
        <StudioText
          id={id}
          text={text}
          displayText={displayText}
          placeholders={placeholders}
          chipNames={chipNames}
          isEditing={isEditing}
          verticalAlign={verticalAlign}
          isFormatBubbleShown={isFormatBubbleShown}
          editorRef={editorRef}
          metricPicker={metricPicker}
          onChange={onChange}
        />
      )}
    </>
  );
};

export default StudioText;
